import React, { useEffect, useState } from "react";
import { useNotify, useRecordContext, Loading, Error, Tab, useTranslate } from "react-admin";

import {
	Box,
	Typography,
	Table,
	TableHead,
	TableRow,
	TableCell,
	TableBody,
	Modal,
	Paper,
	IconButton,
	TextField as MuiTextField,
	Button,
	Chip,
	TablePagination,
	TableSortLabel,
} from "@material-ui/core";
import {
	Close as CloseIcon,
	FilterList as FilterIcon,
	Clear as ClearIcon,
} from "@material-ui/icons";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import { httpClient } from "api/httpClient";
import { palletsUseStyles } from "./Styles";
import { PalletDetails } from "./PalletDetails";
import { BoxDetails } from "./BoxDetails";
import { PalletFilters } from "./PalletsFilter";
import { DateTimeField } from "components/fields/DateFormatField";

export const SortableTableCell = ({ columnKey, currentSort, onSort, label }) => {
	const isSorted = currentSort[0] === columnKey;
	const direction = isSorted ? currentSort[1].toLowerCase() : "asc";

	const handleClick = () => {
		const newDirection = isSorted && direction === "asc" ? "DESC" : "ASC";
		onSort([columnKey, newDirection]);
	};

	return (
		<TableCell sortDirection={isSorted ? direction : false} style={{ cursor: "pointer" }}>
			<TableSortLabel active={isSorted} direction={direction} onClick={handleClick}>
				{label}
			</TableSortLabel>
		</TableCell>
	);
};

export const PalletsTab = (props) => {
	const classes = palletsUseStyles();
	const record = useRecordContext();
	const translate = useTranslate();
	const notify = useNotify();

	const [sort, setSort] = useState(["sscc", "DESC"]);
	const [pallets, setPallets] = useState([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const [selectedPallet, setSelectedPallet] = useState(null);
	const [modalOpen, setModalOpen] = useState(false);
	const [selectedBox, setSelectedBox] = useState(null);
	const [showFilters, setShowFilters] = useState(false);
	const [filters, setFilters] = useState({
		sscc: "",
		gtin: "",
		production_date: "",
		expiration_date: "",
		ext_id: "",
		ext_product_code: "",
		ext_product_description: "",
	});
	const [appliedFilters, setAppliedFilters] = useState({});

	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(25);
	const [total, setTotal] = useState(0);

	const loadPallets = () => {
		if (!record?.id) return;

		setLoading(true);
		const baseFilter = { order_id: record.id };
		const combinedFilter = { ...baseFilter, ...appliedFilters };
		const filterString = JSON.stringify(combinedFilter);

		const start = page * rowsPerPage;
		const end = start + rowsPerPage - 1;
		const rangeString = JSON.stringify([start, end]);

		const sortString = JSON.stringify(sort);

		const url = `/pallets?filter=${encodeURIComponent(filterString)}&range=${encodeURIComponent(
			rangeString
		)}&sort=${encodeURIComponent(sortString)}`;

		httpClient(url)
			.then((response) => {
				const { json, headers } = response;
				setPallets(json);

				const contentRange = headers.get("content-range");
				if (contentRange) {
					const totalMatch = contentRange.match(/\/(\d+)$/);
					if (totalMatch) {
						setTotal(parseInt(totalMatch[1], 10));
					}
				} else {
					setTotal(json.length < rowsPerPage ? start + json.length : start + json.length + 1);
				}

				setLoading(false);
			})
			.catch((err) => {
				console.error(err);
				notify("Error loading pallets", { type: "error" });
				setError(err);
				setLoading(false);
			});
	};

	useEffect(() => {
		loadPallets();
	}, [record, appliedFilters, page, sort, rowsPerPage, notify]);

	const handleRowClick = (pallet) => {
		setSelectedPallet(pallet);
		setSelectedBox(null);
		setModalOpen(true);
	};

	const handleBoxClick = (box) => {
		setSelectedBox(box);
	};

	const handleCloseModal = () => {
		setModalOpen(false);
		setSelectedPallet(null);
		setSelectedBox(null);
	};

	const handleSort = (column) => {
		setSort(column);
		setPage(0); // Reset to first page when sorting
	};

	const handleFilterChange = (field, value) => {
		setFilters((prev) => ({
			...prev,
			[field]: value,
		}));
	};

	const applyFilters = () => {
		const activeFilters = {};
		Object.entries(filters).forEach(([key, value]) => {
			if (value && value.trim() !== "") {
				activeFilters[key] = value.trim();
			}
		});
		setAppliedFilters(activeFilters);
		setPage(0); // Reset to first page when applying filters
	};

	const clearFilters = () => {
		setFilters({
			sscc: "",
			gtin: "",
			production_date: "",
			expiration_date: "",
			ext_id: "",
			ext_product_code: "",
			ext_product_description: "",
		});
		setAppliedFilters({});
		setPage(0); // Reset to first page when clearing filters
	};

	const removeFilter = (filterKey) => {
		const newAppliedFilters = { ...appliedFilters };
		delete newAppliedFilters[filterKey];
		setAppliedFilters(newAppliedFilters);

		setFilters((prev) => ({
			...prev,
			[filterKey]: "",
		}));
		setPage(0); // Reset to first page when removing filter
	};

	const handleChangePage = (event, newPage) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (event) => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
	};

	return (
		<Tab label="Pallets" {...props}>
			<Box p={2}>
				<Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
					<Typography variant="h6" gutterBottom>
						{translate("tab.pallets.pallets_for_order")} #{record?.id}
					</Typography>
					<Button
						startIcon={<FilterIcon />}
						onClick={() => setShowFilters(!showFilters)}
						variant="outlined"
						size="small"
					>
						{translate("tab.pallets.filters")}
					</Button>
				</Box>

				{Object.keys(appliedFilters).length > 0 && (
					<Box mb={2}>
						<Typography variant="subtitle2" gutterBottom>
							Active Filters:
						</Typography>
						{Object.entries(appliedFilters).map(([key, value]) => (
							<Chip
								key={key}
								label={`${key}: ${value}`}
								onDelete={() => removeFilter(key)}
								className={classes.activeFilter}
								size="small"
								color="primary"
								variant="outlined"
							/>
						))}
					</Box>
				)}

				{/* Filter Panel */}
				{showFilters && (
					<PalletFilters
						translate={translate}
						filters={filters}
						onFilterChange={handleFilterChange}
						onApplyFilters={applyFilters}
						onClearFilters={clearFilters}
						classes={classes}
					/>
				)}

				{loading ? (
					<Loading />
				) : error ? (
					<Error />
				) : pallets.length === 0 ? (
					<Typography>{translate("tab.pallets.no_pallets_found")}</Typography>
				) : (
					<>
						<Table size="small">
							<TableHead>
								<TableRow>
									<TableCell>{translate("tab.pallets.pallet_id")}</TableCell>
									<SortableTableCell
										columnKey="sscc"
										label={translate("tab.pallets.sscc")}
										currentSort={sort}
										onSort={handleSort}
									/>
									<SortableTableCell
										columnKey="gtin"
										label={translate("tab.pallets.gtin")}
										currentSort={sort}
										onSort={handleSort}
									/>
									<SortableTableCell
										columnKey="production_date"
										label={translate("tab.pallets.production_date")}
										currentSort={sort}
										onSort={handleSort}
									/>
									<SortableTableCell
										columnKey="expiration_date"
										label={translate("tab.pallets.expiration_date")}
										currentSort={sort}
										onSort={handleSort}
									/>
									<SortableTableCell
										columnKey="quantity"
										label={translate("tab.pallets.quantity")}
										currentSort={sort}
										onSort={handleSort}
									/>
									<SortableTableCell
										columnKey="net_weight"
										label={translate("tab.pallets.weight")}
										currentSort={sort}
										onSort={handleSort}
									/>
									<SortableTableCell
										columnKey="weight_unit_code"
										label={translate("tab.pallets.weight_unit_code")}
										currentSort={sort}
										onSort={handleSort}
									/>
									<SortableTableCell
										columnKey="number_of_pallet"
										label={translate("tab.pallets.number_of_pallet")}
										currentSort={sort}
										onSort={handleSort}
									/>
									<SortableTableCell
										columnKey="total_number_of_pallet"
										label={translate("tab.pallets.total_pallets")}
										currentSort={sort}
										onSort={handleSort}
									/>
									<SortableTableCell
										columnKey="ext_id"
										label={translate("tab.pallets.external_id")}
										currentSort={sort}
										onSort={handleSort}
									/>
									<SortableTableCell
										columnKey="ext_product_code"
										label={translate("tab.pallets.product_code")}
										currentSort={sort}
										onSort={handleSort}
									/>
									<SortableTableCell
										columnKey="ext_product_description"
										label={translate("tab.pallets.product_description")}
										currentSort={sort}
										onSort={handleSort}
									/>
								</TableRow>
							</TableHead>
							<TableBody>
								{pallets.map((pallet) => (
									<TableRow
										key={pallet.id}
										className={classes.tableRow}
										onClick={() => handleRowClick(pallet)}
									>
										<TableCell>{pallet.id}</TableCell>
										<TableCell>{pallet.sscc}</TableCell>
										<TableCell>{pallet.gtin}</TableCell>
										<TableCell>
											<DateTimeField source="production_date" withLocalTimeZone record={pallet} />
										</TableCell>
										<TableCell>
											<DateTimeField source="expiration_date" withLocalTimeZone record={pallet} />
										</TableCell>
										<TableCell>{pallet.quantity}</TableCell>
										<TableCell>{pallet.net_weight}</TableCell>
										<TableCell>{pallet.weight_unit_code}</TableCell>
										<TableCell>{pallet.number_of_pallet}</TableCell>
										<TableCell>{pallet.total_number_of_pallet}</TableCell>
										<TableCell>{pallet.ext_id}</TableCell>
										<TableCell>{pallet.ext_product_code}</TableCell>
										<TableCell>{pallet.ext_product_description}</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>

						{/* Pagination */}
						<TablePagination
							rowsPerPageOptions={[10, 25, 50, 100]}
							component="div"
							count={total}
							rowsPerPage={rowsPerPage}
							page={page}
							onChangePage={handleChangePage}
							onChangeRowsPerPage={handleChangeRowsPerPage}
							labelRowsPerPage={translate("ra.navigation.page_rows_per_page")}
							labelDisplayedRows={({ from, to, count }) =>
								translate("ra.navigation.page_range_info", {
									offsetBegin: from,
									offsetEnd: to,
									total: count,
								})
							}
						/>
					</>
				)}

				{/* Modal */}
				<Modal open={modalOpen} onClose={handleCloseModal} className={classes.modal}>
					<Paper className={classes.modalContent}>
						<div className={classes.modalHeader}>
							{selectedBox && (
								<IconButton onClick={() => setSelectedBox(null)}>
									<ArrowBackIcon />
								</IconButton>
							)}
							<Typography variant="h5">{selectedBox ? "Box Details" : "Pallet Details"}</Typography>
							<IconButton onClick={handleCloseModal}>
								<CloseIcon />
							</IconButton>
						</div>

						{selectedBox ? (
							<BoxDetails translate={translate} classes={classes} selectedBox={selectedBox} />
						) : (
							<PalletDetails
								translate={translate}
								classes={classes}
								selectedPallet={selectedPallet}
								handleBoxClick={handleBoxClick}
								selectedBox={selectedBox}
							/>
						)}
					</Paper>
				</Modal>
			</Box>
		</Tab>
	);
};
